import { HTMLProps, useContext, useEffect, useRef, useState } from 'react';
import { Forecast } from '.';
import { IconElements } from '../../../config/icons';
import { ForecastContext } from '../../../contexts/investment-robot/ForecastContext';
import { GoalIndicationContext } from '../../../contexts/investment-robot/GoalIndicationContext';
import { Button } from '../../general/Button';
import { useTranslation } from 'react-i18next';
import { EmailMeModal } from './email-me-modal/EmailMeModal';
import { useHistory, useLocation } from 'react-router';

export const ForecastScreen: React.FC<{}> = ({ children }) => {
    const { forecast } = useContext(ForecastContext);
    const goalIndicationContext = useContext(GoalIndicationContext);
    const { t } = useTranslation('investment');
    const [isEmailMeModalShown, setIsEmailMeModalShown] = useState(false);
    const history = useHistory();
    const location = useLocation<{skipGoalIndication?: boolean}>();
    const [isEmailSent, setIsEmailSent] = useState(false);

    const resultsRef = useRef<HTMLDivElement>(undefined!);

    const showResult = !forecast.query.isError && !forecast.query.isLoading ? '' : 'hidden';

    useEffect(() => {
        const skipGoalIndication =
            new URLSearchParams(window.location.search).get('skipGoalIndication') === 'true' ? true : false;
        // Do not propagate if the goal indication flow is not walked through.
        if ( !(location.state?.skipGoalIndication) && !skipGoalIndication) {
            goalIndicationContext.actions.propagate();
        }

        resultsRef.current.scrollIntoView();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div id="forecast-screen" ref={resultsRef}>
            <EmailMeModal
                isShown={isEmailMeModalShown}
                setIsEmailSent={setIsEmailSent}
                onClose={() => {
                    setIsEmailMeModalShown(false);
                }}
            />
            <div className="grid lg:grid-cols-4 grid-cols-1 gap-2 xl:gap-8 shadow-2xl lg:shadow-none">
                <div className="lg:col-span-3 col-span-1">
                    <Forecast.ChartSection className="h-full" />
                </div>
                <div className="lg:flex hidden col-span-1">
                    <Forecast.ResultsSection />
                </div>
            </div>
            <div
                className={`relative mt-12 flex flex-col lg:flex-row items-center justify-center ${showResult}`}
            >
                <SmallLettersDisclosure className="lg:mt-0 sm:ml-0 lg:absolute lg:right-0 lg:mr-8 px-4" />
                <div className="self-stretch mt-16 mx-4 grid grid-rows-2 gap-4 lg:hidden">
                    <Button
                        className="shadow-lg"
                        onClick={() => {
                            setIsEmailMeModalShown(true);
                        }}
                    >
                        <div className="flex flex-row justify-center items-center">
                            <IconElements.Banner className="w-6 h-6 mr-2 " />
                            {t('forecast-screen-result-save-calculation')}
                        </div>
                    </Button>
                    {isEmailSent && (
                        <div className="flex flex-row justify-center items-center text-bnd-green">
                            {t('forecast-screen-result-email-sent')}
                        </div>
                    )}
                    <Button
                        tint="sober"
                        className=""
                        onClick={() => {
                            history.push('/');
                        }}
                    >
                        Begin opnieuw
                    </Button>
                </div>
                <div className="flex flex-1 flex-row justify-center items-center">
                    <Button
                        tint="green"
                        className=""
                        onClick={() => {
                            history.push('/summary');
                        }}
                    >
                        Volgende
                    </Button>
                </div>
            </div>
        </div>
    );
};

const SmallLettersDisclosure: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ className }) => {
    const { t } = useTranslation('investment');

    const [isCollapsed, setIsCollapsed] = useState(false);

    return (
        <div
            className={`text-black flex flex-col items-baseline lg:max-w-lg ${
                isCollapsed ? 'lg:bg-white lg:py-4 lg:rounded-lg lg:shadow-lg z-100' : ''
            }   ${className}`}
        >
            <div className="flex flex-row items-baseline">
                <div className="text-base lg:text-lg font-semibold">{t('forecast-screen-small-letters-title')}</div>
            </div>
            <div
                className="text-sm text-gray-700"
                onClick={() => {
                    setIsCollapsed(!isCollapsed);
                }}
            >
                {!isCollapsed && (
                    <>
                        <span>{t('forecast-screen-small-letters-description-short')}</span>
                        <span className="ml-2 text-bnd-green underline cursor-pointer">Lees meer</span>
                    </>
                )}
                {isCollapsed && (
                    <>
                        <span>{t('forecast-screen-small-letters-description')}</span>
                        <span className="ml-2 text-bnd-green underline cursor-pointer">Sluiten</span>
                    </>
                )}
            </div>
        </div>
    );
};
